import { EMAIL_PLATFORMS, UNRESOLVED_USER_ID } from '@this+that/constants';
import {
  LabelId,
  MessagingPlatform,
  UserCommunication,
} from '@this+that/models';
import { aggregateThreadLabelIds } from '@this+that/utils';

import { UNCONFIRMED_DRAFT_TTL_MS } from './constants/drafts';
import { sortCommunicationsByDate } from './sortUtils';

export const withInboxEntryLink = (
  communication: Partial<UserCommunication>,
  entryId: string,
): UserCommunication =>
  ({
    ...communication,
    inboxEntryIds: Array.from(
      new Set([...(communication.inboxEntryIds ?? []), entryId]),
    ),
  }) as UserCommunication;

export const isSentOrDraft = (
  userCommunication: UserCommunication,
): boolean => {
  return (
    userCommunication.labelIds?.some((id) =>
      [LabelId.SENT, LabelId.DRAFT].includes(id),
    ) ?? false
  );
};

export const isEmailCommunication = (
  communication: Partial<UserCommunication>,
): boolean =>
  communication.platform !== undefined &&
  EMAIL_PLATFORMS.includes(communication.platform);

export const isUnreadEmailCommunication = (
  communication: Partial<UserCommunication>,
): boolean =>
  isEmailCommunication(communication) &&
  (communication.labelIds?.includes(LabelId.UNREAD) ?? false);

// Email read state belongs to the mailbox owner; teammates see it read-only.
// Chat platforms carry no per-viewer read state, so they never block an action.
export const isOwnMailboxCommunication = (
  communication: Partial<UserCommunication>,
  viewerId: string | null | undefined,
): boolean =>
  !isEmailCommunication(communication) ||
  (!!viewerId && communication.userId === viewerId);

export const isInInbox = (userCommunication: UserCommunication): boolean => {
  return userCommunication.labelIds?.includes(LabelId.INBOX) ?? false;
};

export const hasLinkedDoBox = (
  communication: Pick<UserCommunication, 'inboxEntryIds'>,
): boolean => (communication.inboxEntryIds?.length ?? 0) > 0;

export const isDraft = (userCommunication: UserCommunication): boolean => {
  return (
    Boolean(userCommunication.draftId) ||
    (userCommunication.labelIds?.includes(LabelId.DRAFT) ?? false)
  );
};

// Epoch ms can arrive as a number or a numeric string, and the web also carries ISO strings.
const communicationTime = (
  createdAt: Partial<UserCommunication>['createdAt'] | string,
): number => {
  const asNumber = Number(createdAt);
  const time = Number.isNaN(asNumber)
    ? new Date(createdAt as string).getTime()
    : asNumber;
  return Number.isNaN(time) ? Number.NEGATIVE_INFINITY : time;
};

// By date, not position: the detail reorders threadMessages to follow its list.
export const findNewestCommunication = <T extends Partial<UserCommunication>>(
  communications: readonly T[] | null | undefined,
): T | undefined =>
  communications?.length
    ? communications.reduce((newest, candidate) =>
        communicationTime(candidate.createdAt) >
        communicationTime(newest.createdAt)
          ? candidate
          : newest,
      )
    : undefined;

// The list row for a thread is always its newest message: when a reply
// arrives after the draft was last saved, the row itself carries no
// draftId even though it's still labeled DRAFT — the real draft is nested
// in threadMessages.
export const resolveDraftCommunication = (
  uc: UserCommunication,
): UserCommunication | undefined => {
  if (uc.draftId) {
    return uc;
  }
  return findNewestCommunication(
    uc.threadMessages?.filter((message) => message.draftId),
  );
};

export const resolveDraftId = (uc: UserCommunication): string | undefined => {
  return resolveDraftCommunication(uc)?.draftId;
};

export const isTrashedOrSpam = (
  userCommunication: UserCommunication,
): boolean => {
  return (
    userCommunication.labelIds?.some((id) =>
      [LabelId.TRASH, LabelId.SPAM].includes(id),
    ) ?? false
  );
};

// Shared by the DoBox card dropdown and the entry details modal — trash/spam
// (including an optimistic pre-confirmation label write) must never render
// as its own row.
export const filterVisibleCommunications = <
  T extends Partial<UserCommunication>,
>(
  communications: T[] | undefined,
): T[] =>
  (communications ?? []).filter(
    (comm) => comm.messageId && !isTrashedOrSpam(comm as UserCommunication),
  );

// The DRAFT list query is a thread query: a row's top-level draftId is unset
// whenever the thread's newest message is a non-draft reply, and the actual
// draft sits inside its threadMessages instead.
export const resolveServerRowDraftId = (
  row: Partial<UserCommunication>,
): string | undefined =>
  row.draftId ??
  findNewestCommunication(row.threadMessages?.filter((m) => m.draftId))
    ?.draftId;

// A draft has no WebSocket channel: the server suppresses INSERT/MODIFY for
// it, so the local optimistic save is the only source of truth until a
// refetch's own row proves the server caught up. TTL-bounded: past
// UNCONFIRMED_DRAFT_TTL_MS since the local save the mark is stale regardless
// of whether a confirming row ever arrived, so the server (present or
// absent) becomes the truth again.
export const isFreshUnconfirmedDraft = (
  unconfirmedDrafts: Record<string, number>,
  communication: Partial<UserCommunication>,
  now: number,
): boolean =>
  !!communication.draftId &&
  communication.draftId in unconfirmedDrafts &&
  now - unconfirmedDrafts[communication.draftId] <= UNCONFIRMED_DRAFT_TTL_MS;

export const rowCarriesDraft = (
  row: Partial<UserCommunication>,
  draftId: string,
): boolean =>
  row.draftId === draftId ||
  !!row.threadMessages?.some((m) => m.draftId === draftId);

// Mirrors rowCarriesDraft for a plain messageId lookup: the DRAFT/thread list
// row is always the thread's newest message, so an older message the caller
// asks for by id can be nested in threadMessages instead of at the top level.
export const rowCarriesMessage = (
  row: Partial<UserCommunication>,
  messageId: string,
): boolean =>
  row.messageId === messageId ||
  !!row.threadMessages?.some((m) => m.messageId === messageId);

// A row that isn't itself the deleted draft can still carry it nested in its
// own threadMessages (the DRAFT list query's thread-head-is-a-reply case) —
// that entry must be dropped too, and the DRAFT label with it once no
// remaining thread message still carries a draftId.
export const stripThreadCarriedDraft = <T extends Partial<UserCommunication>>(
  msg: T,
  draftId: string,
): T => {
  if (msg.draftId === draftId || !rowCarriesDraft(msg, draftId)) {
    return msg;
  }

  const remainingThreadMessages = (msg.threadMessages ?? []).filter(
    (threadMessage) => threadMessage.draftId !== draftId,
  );
  const stillHasDraft = remainingThreadMessages.some(
    (threadMessage) => !!threadMessage.draftId,
  );

  return {
    ...msg,
    threadMessages: remainingThreadMessages,
    labelIds: stillHasDraft
      ? msg.labelIds
      : msg.labelIds?.filter((label) => label !== LabelId.DRAFT),
  };
};

// The server shapes a thread's row as its newest message, so removing a draft
// that IS the row (or carries it nested) can strand the thread's other
// messages — this rebuilds the row from what's left, promoting the newest
// remaining message to head. Excludes every draft nested in the thread, not
// only the one being deleted — the server can still hold a stale, older draft
// the Gmail webhook hasn't caught up on deleting, and that must never be
// promoted to head in its place. Returns null when nothing remains to restore.
export const buildThreadRestoreUserCommunication = <
  T extends Partial<UserCommunication>,
>(
  draftToBeDeleted: T,
  draftId: string,
): T | null => {
  const nonDraftMessages = (draftToBeDeleted.threadMessages ?? []).filter(
    (msg) => msg.messageId !== draftId && !isDraft(msg as UserCommunication),
  );

  if (nonDraftMessages.length === 0) {
    return null;
  }

  const lastMessage = nonDraftMessages.at(0);
  if (!lastMessage) {
    return null;
  }

  const otherMessages = nonDraftMessages.slice(1);
  const inboxEntryIds = Array.from(
    new Set([
      ...(draftToBeDeleted.inboxEntryIds ?? []),
      ...nonDraftMessages.flatMap((msg) => msg.inboxEntryIds ?? []),
    ]),
  );

  return {
    ...lastMessage,
    labelIds: aggregateThreadLabelIds(
      lastMessage.labelIds || [],
      otherMessages,
    ),
    threadMessages: otherMessages,
    inboxEntryIds: inboxEntryIds.length > 0 ? inboxEntryIds : undefined,
  } as T;
};

/**
 * The newest email message in the thread that the mailbox owner did NOT
 * send (Gmail's own SENT label, via isSentOrDraft) — Reply/Reply-all must
 * always target this, never the owner's own last-sent message, or the
 * composer ends up addressed to yourself.
 */
export const resolveLatestCounterpartEmailMessageId = (
  communications: Partial<UserCommunication>[] | null | undefined,
): string | null => {
  if (!communications || communications.length === 0) {
    return null;
  }
  const counterpartEmails = communications.filter(
    isCounterpartEmailCommunication,
  );
  if (counterpartEmails.length === 0) {
    return null;
  }
  const [latest] = sortCommunicationsByDate(counterpartEmails);
  return latest?.messageId ?? null;
};

function isCounterpartEmailCommunication(
  communication: Partial<UserCommunication>,
): boolean {
  return (
    isEmailCommunication(communication) &&
    !!communication.messageId &&
    !isSentOrDraft(communication as UserCommunication)
  );
}

// `authorUserId` holds one of three things: a resolved id, the sender's own
// address when nobody matched, or UNRESOLVED_USER_ID when the lookup failed.
// The last two both mean "we cannot say this came from someone else", so
// neither may count as a counterpart — same guard as isUnread in
// InboxConversationCard.tsx. slackWorkspaceId/channel are required up front
// so the reply entry point never appears for a row that can't be sent to.
function isCounterpartSlackCommunication(
  communication: Partial<UserCommunication>,
): boolean {
  return (
    communication.platform === MessagingPlatform.SLACK &&
    !!communication.messageId &&
    !!communication.slackWorkspaceId &&
    !!communication.channel &&
    communication.authorUserId !== communication.userId &&
    communication.authorUserId !== communication.userEmailAddress &&
    communication.authorUserId !== UNRESOLVED_USER_ID
  );
}

export interface LatestCounterpartCommunicationRef {
  messageId: string;
  platform: MessagingPlatform;
}

/**
 * The newest counterpart row across both email and Slack — the deterministic
 * Reply entry point's target resolution. A direct max-by-createdAt, not
 * sortCommunicationsByDate: that helper's chat-vs-email ordering direction
 * depends on `a.platform` per compared pair, which is inconsistent for "pick
 * the single latest row" across a mixed list.
 */
export const resolveLatestCounterpartCommunication = (
  communications: Partial<UserCommunication>[] | null | undefined,
): LatestCounterpartCommunicationRef | null => {
  if (!communications || communications.length === 0) {
    return null;
  }
  const counterparts = communications.filter(
    (communication) =>
      isCounterpartEmailCommunication(communication) ||
      isCounterpartSlackCommunication(communication),
  );
  if (counterparts.length === 0) {
    return null;
  }
  const latest = findNewestCommunication(counterparts);
  if (!latest?.messageId || !latest.platform) {
    return null;
  }
  return { messageId: latest.messageId, platform: latest.platform };
};
